var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, AlertTitle, Box, Button, Typography } from "@mui/material";
import { PageLimit, theme } from "@sequentech/ui-essentials";
import { translate } from "@sequentech/ui-core";
import { useTranslation } from "react-i18next";
import { SettingsContext } from "../providers/SettingsContextProvider";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { selectFirstBallotStyle } from "../store/ballotStyles/ballotStylesSlice";
import { updateBallotStyleAndSelection } from "./PreviewPublicationEvent";
/**
 * Where a preview handed over as a file gets hydrated, and where it is kept so
 * it survives the reload the auth path does.
 *
 * `sessionStorage` rather than `localStorage`: a preview is a working document
 * for one tab, and leaving somebody's unpublished ballot in a browser after they
 * close it is not something a preview screen should do.
 */
export var PREVIEW_FILE_KEY = "previewFromFile";
export var PREVIEW_FILE_AREA_KEY = "previewFromFileArea";
/** What is wrong with the file, in words somebody can act on, or `null`. */
export var rejectPreviewDocument = function (document) {
    if (typeof document !== "object" || document === null) {
        return "That file does not contain a JSON object.";
    }
    var carrier = document;
    if (!Array.isArray(carrier.ballot_styles)) {
        return "That file has no `ballot_styles`, so it is not a ballot preview.";
    }
    if (carrier.ballot_styles.length === 0) {
        return "That preview contains no ballots. Nobody would be given one.";
    }
    if (typeof carrier.election_event !== "object" || carrier.election_event === null) {
        return "That file has no `election_event`.";
    }
    // Hydration iterates these after it has stored the event, so a missing one
    // would leave half an event loaded behind a "not iterable" error.
    for (var _i = 0, _a = ["elections", "documents", "support_materials"]; _i < _a.length; _i++) {
        var key = _a[_i];
        if (!Array.isArray(carrier[key])) {
            return "That file has no `".concat(key, "` list.");
        }
    }
    return null;
};
var choices = function (document, language) {
    var _a, _b;
    var byArea = new Map();
    for (var _i = 0, _c = document.ballot_styles; _i < _c.length; _i++) {
        var style = _c[_i];
        var contests = (_a = style.contests) !== null && _a !== void 0 ? _a : [];
        var named = contests.map(function (contest) { return translate(contest, "name", language) || contest.id; });
        byArea.set(style.area_id, __spreadArray(__spreadArray([], ((_b = byArea.get(style.area_id)) !== null && _b !== void 0 ? _b : []), true), named, true));
    }
    return Array.from(byArea.entries()).map(function (_a) {
        var areaId = _a[0], contests = _a[1];
        return ({
            areaId: areaId,
            // The document carries no area names — a ballot style names its area by
            // id — so it is labelled by what is on it, which is the thing somebody
            // previewing actually wants to tell the ballots apart by.
            label: contests.length > 0 ? contests.join(", ") : "an empty ballot",
        });
    });
};
/**
 * Open a ballot preview from a file.
 *
 * The sibling of [`PreviewPublicationEvent`], which fetches the same document
 * from the public bucket after the Admin Portal has published it there. This one
 * takes the file directly, which is what makes it useful before anything has
 * been imported: `step-cli step compile-plan --preview` writes exactly this
 * document, and so does the Election Architect's review step.
 *
 * The file is read in the browser. Nothing is uploaded, which for an unpublished
 * ballot is the point rather than a detail.
 *
 * It is deliberately not a URL or a `postMessage` listener. This page is
 * unauthenticated and frameable, so a channel that let another origin push a
 * document into it would be new attack surface for a convenience — and a file
 * the operator picks is a document they have already seen.
 */
export var PreviewFromFile = function () {
    var _a = useTranslation(), t = _a.t, i18n = _a.i18n;
    var globalSettings = useContext(SettingsContext).globalSettings;
    var navigate = useNavigate();
    var dispatch = useAppDispatch();
    var ballotStyle = useAppSelector(selectFirstBallotStyle);
    var _b = useState(null), document_ = _b[0], setDocument = _b[1];
    var _c = useState(null), failure = _c[0], setFailure = _c[1];
    // A preview opened before signing in has to survive `App`'s reload, which is
    // how the authenticated path gets a session. Held in `sessionStorage` and
    // read back here.
    useEffect(function () {
        var held = sessionStorage.getItem(PREVIEW_FILE_KEY);
        if (held === null || document_ !== null) {
            return;
        }
        try {
            var parsed = JSON.parse(held);
            if (rejectPreviewDocument(parsed) !== null) {
                sessionStorage.removeItem(PREVIEW_FILE_KEY);
                return;
            }
            var area = sessionStorage.getItem(PREVIEW_FILE_AREA_KEY);
            setDocument(parsed);
            if (area !== null) {
                hydrate(parsed, area);
            }
        }
        catch (_a) {
            sessionStorage.removeItem(PREVIEW_FILE_KEY);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(function () {
        if ((ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.election_event_id) && ballotStyle.tenant_id) {
            navigate("/tenant/".concat(ballotStyle.tenant_id, "/event/").concat(ballotStyle.election_event_id, "/election-chooser"));
        }
    }, [ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.election_event_id, ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.tenant_id, navigate]);
    var read = function (file) { return __awaiter(void 0, void 0, Promise, function () {
        var parsed, _a, _b, wrong, error_1;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    _c.trys.push([0, 2, , 3]);
                    _b = (_a = JSON).parse;
                    return [4 /*yield*/, file.text()];
                case 1:
                    parsed = _b.apply(_a, [_c.sent()]);
                    wrong = rejectPreviewDocument(parsed);
                    if (wrong !== null) {
                        setFailure(wrong);
                        setDocument(null);
                        return [2 /*return*/];
                    }
                    setFailure(null);
                    setDocument(parsed);
                    return [3 /*break*/, 3];
                case 2:
                    error_1 = _c.sent();
                    setDocument(null);
                    setFailure(error_1 instanceof Error ? error_1.message : String(error_1));
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); };
    var hydrate = function (preview, areaId) {
        var _a, _b;
        var tenantId = (_b = (_a = preview.ballot_styles[0]) === null || _a === void 0 ? void 0 : _a.tenant_id) !== null && _b !== void 0 ? _b : "";
        try {
            sessionStorage.setItem("isDemo", "true");
            sessionStorage.setItem(PREVIEW_FILE_AREA_KEY, areaId);
            sessionStorage.setItem(PREVIEW_FILE_KEY, JSON.stringify(preview));
        }
        catch (_c) {
            // Over the quota. Worth saying rather than failing later at the
            // reload, where it would look like the preview simply did not work.
            if (!globalSettings.DISABLE_AUTH) {
                setFailure("That preview is too large to keep across signing in. It " +
                    "will open, but if you are asked to sign in you will " +
                    "have to choose the file again.");
            }
        }
        try {
            updateBallotStyleAndSelection(preview, tenantId, areaId, dispatch);
        }
        catch (error) {
            setFailure("That ballot could not be loaded: ".concat(error instanceof Error ? error.message : String(error)));
        }
    };
    return (_jsx(PageLimit, { maxWidth: "md", className: "preview-from-file-screen screen", children: _jsxs(Box, { className: "preview-from-file-content", sx: { display: "flex", flexDirection: "column", gap: 2, py: 4 }, children: [_jsx(Typography, { className: "screen-title", variant: "h4", component: "h1", children: t("previewFromFile.title", "Preview a ballot") }), _jsx(Typography, { className: "screen-description", variant: "body1", color: theme.palette.customGrey.contrastText, children: t("previewFromFile.blurb", "Open a ballot preview file to see the ballot exactly as voters will. The file is read here in your browser — nothing is uploaded — and the ballots it contains cannot be voted on.") }), _jsx(Box, { className: "preview-file-picker", children: _jsxs(Button, { className: "preview-file-button", variant: "contained", component: "label", children: [t("previewFromFile.choose", "Choose a preview file"), _jsx("input", { className: "preview-file-input", hidden: true, type: "file", accept: "application/json,.json", "data-testid": "preview-file", onChange: function (event) {
                                    var _a;
                                    var file = (_a = event.target.files) === null || _a === void 0 ? void 0 : _a[0];
                                    if (file !== undefined) {
                                        void read(file);
                                    }
                                    event.target.value = "";
                                } })] }) }), failure !== null && (_jsx(Alert, { className: "preview-file-error", severity: "error", "data-testid": "preview-file-failed", children: failure })), document_ !== null && (_jsxs(Box, { className: "preview-file-ballots", sx: { display: "flex", flexDirection: "column", gap: 1 }, children: [_jsxs(Alert, { className: "preview-file-notice", severity: "info", children: [_jsx(AlertTitle, { className: "preview-file-notice-title", children: t("previewFromFile.notReal", "This is a preview, not an election") }), t("previewFromFile.notRealHelp", "The key it carries is a stand-in, so nothing you do here is recorded and no vote can be cast.")] }), _jsx(Typography, { className: "preview-file-pick", variant: "h6", component: "h2", children: t("previewFromFile.pick", "Which ballot?") }), choices(document_, i18n.language).map(function (choice) { return (_jsx(Button, { className: "preview-file-ballot-button", variant: "outlined", sx: { justifyContent: "flex-start" }, "data-testid": "preview-area-".concat(choice.areaId), onClick: function () { return hydrate(document_, choice.areaId); }, children: choice.label }, choice.areaId)); })] }))] }) }));
};
export default PreviewFromFile;
